/**
 * CompanySettingsSection — manages Partner Code, Bank Account, VAT Rate, and the
 * Billed-To dropdown list. Used inside AccountSettings.js as `tab=partner-code` or
 * `tab=bank-account`.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2, Save, KeyRound, Landmark, Percent, Building2, Plus, Trash2, X,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { getAuthHeaders } from '../../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompanySettingsSection({ language, mode = 'partner-code' }) {
  // mode: 'partner-code' | 'bank-account'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showAddBilledTo, setShowAddBilledTo] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/company-settings`, { headers: getAuthHeaders() });
      setSettings(res.data);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل تحميل الإعدادات' : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const savePartnerCode = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/company-settings/partner-code`,
        { partner_code: settings.partner_code || '' },
        { headers: getAuthHeaders() },
      );
      toast.success(language === 'ar' ? 'تم حفظ كود الشريك' : 'Partner code saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const saveVatRate = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/company-settings/vat-rate`,
        { vat_rate: parseFloat(settings.vat_rate) || 0 },
        { headers: getAuthHeaders() },
      );
      toast.success(language === 'ar' ? 'تم حفظ نسبة الضريبة' : 'VAT rate saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const saveBank = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/company-settings/bank-account`,
        settings.bank_account,
        { headers: getAuthHeaders() },
      );
      toast.success(language === 'ar' ? 'تم حفظ بيانات الحساب البنكي' : 'Bank account saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const setBankField = (key, val) => setSettings((s) => ({ ...s, bank_account: { ...s.bank_account, [key]: val } }));

  if (loading || !settings) {
    return <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#d4af37] mx-auto" /></div>;
  }

  // ===== Partner Code mode =====
  if (mode === 'partner-code') {
    return (
      <div className="space-y-6" data-testid="partner-code-section">
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6">
          <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#d4af37]" />
            {language === 'ar' ? 'كود الشريك (RAKEZ Partner Code)' : 'Partner Code (RAKEZ)'}
          </h2>
          <p className="text-sm text-[#A1A1AA] mb-6">
            {language === 'ar'
              ? 'سيتم استخدام هذا الكود في كل فاتورة ضريبية تُنشأ من قسم المالية.'
              : 'This code will be used on every tax invoice generated under Finance.'}
          </p>

          <div className="max-w-md space-y-2">
            <Label className="text-[#A1A1AA] text-xs">{language === 'ar' ? 'كود الشريك' : 'Channel Partner Code'}</Label>
            <Input
              value={settings.partner_code || ''}
              onChange={(e) => setSettings((s) => ({ ...s, partner_code: e.target.value }))}
              placeholder="0004012295"
              className="bg-[#0a0a0a] border-[#2a2a4e] text-white font-mono"
              data-testid="partner-code-input"
            />
            <Button
              onClick={savePartnerCode}
              disabled={saving}
              className="mt-3 bg-[#d4af37] hover:bg-[#c9a227] text-black"
              data-testid="save-partner-code-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Save className="h-4 w-4 me-2" />}
              {language === 'ar' ? 'حفظ' : 'Save'}
            </Button>
          </div>
        </div>

        {/* VAT rate */}
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6">
          <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
            <Percent className="h-5 w-5 text-[#10B981]" />
            {language === 'ar' ? 'نسبة الضريبة (VAT %)' : 'VAT Rate (%)'}
          </h2>
          <p className="text-sm text-[#A1A1AA] mb-4">
            {language === 'ar'
              ? 'النسبة التي ستُستخدم عند تفعيل خيار "خاضع للضريبة" في صف الفاتورة.'
              : 'Used when the "Taxable" toggle is on for an invoice row.'}
          </p>
          <div className="max-w-xs flex gap-2">
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={settings.vat_rate}
              onChange={(e) => setSettings((s) => ({ ...s, vat_rate: e.target.value }))}
              className="bg-[#0a0a0a] border-[#2a2a4e] text-white font-mono"
              data-testid="vat-rate-input"
            />
            <Button onClick={saveVatRate} disabled={saving} className="bg-[#10B981] hover:bg-emerald-600 text-white">
              <Save className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Billed-To list */}
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#0EA5E9]" />
                {language === 'ar' ? 'قائمة الجهات (Billed To)' : 'Billed-To List'}
              </h2>
              <p className="text-sm text-[#A1A1AA] mt-1">
                {language === 'ar'
                  ? 'هذه القائمة ستظهر في القائمة المسدلة عند إنشاء فاتورة جديدة.'
                  : 'These entries appear in the Billed-To dropdown when creating an invoice.'}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setShowAddBilledTo(true)}
              className="bg-[#d4af37] hover:bg-[#c9a227] text-black"
              data-testid="add-billed-to-btn"
            >
              <Plus className="h-4 w-4 me-1" />
              {language === 'ar' ? 'إضافة جهة' : 'Add entity'}
            </Button>
          </div>

          <div className="space-y-2">
            {(settings.billed_to_list || []).map((b) => (
              <div key={b.id} className="p-3 bg-[#0a0a0a] border border-white/10 rounded-xl flex items-start justify-between gap-3" data-testid={`billed-to-row-${b.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">{b.name}</p>
                  {b.trn && <p className="text-xs text-[#71717A] mt-0.5">TRN: {b.trn}</p>}
                  {(b.address_lines || []).map((line, i) => (
                    <p key={i} className="text-xs text-[#71717A]">{line}</p>
                  ))}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    if (!window.confirm(language === 'ar' ? 'حذف هذه الجهة؟' : 'Delete this entity?')) return;
                    try {
                      await axios.delete(`${BACKEND_URL}/api/admin/company-settings/billed-to/${b.id}`, { headers: getAuthHeaders() });
                      toast.success(language === 'ar' ? 'تم الحذف' : 'Deleted');
                      load();
                    } catch (err) { toast.error('Failed'); }
                  }}
                  className="h-8 border-red-500/30 text-red-400 hover:bg-red-500/10"
                  data-testid={`del-billed-to-${b.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            {(settings.billed_to_list || []).length === 0 && (
              <p className="text-center text-[#808080] py-6 text-sm">
                {language === 'ar' ? 'لا توجد جهات مضافة بعد' : 'No entities yet'}
              </p>
            )}
          </div>
        </div>

        {/* Add Billed-To modal */}
        <AddBilledToModal
          open={showAddBilledTo}
          onClose={() => setShowAddBilledTo(false)}
          onSaved={() => { setShowAddBilledTo(false); load(); }}
          language={language}
        />
      </div>
    );
  }

  // ===== Bank Account mode =====
  return (
    <div className="space-y-6" data-testid="bank-account-section">
      <div className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6">
        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          <Landmark className="h-5 w-5 text-[#d4af37]" />
          {language === 'ar' ? 'الحساب المالي للشركة' : 'Company Bank Account'}
        </h2>
        <p className="text-sm text-[#A1A1AA] mb-6">
          {language === 'ar'
            ? 'هذه البيانات ستظهر في قسم "Payment Details" في كل فاتورة ضريبية.'
            : 'These details appear in the "Payment Details" section of every tax invoice.'}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
          {[
            { key: 'beneficiary_name', ar: 'اسم المستفيد', en: 'Beneficiary Name', placeholder: 'CLOUDGM FZ LLC' },
            { key: 'account_number', ar: 'رقم الحساب', en: 'Account Number', placeholder: '8023605457901' },
            { key: 'iban', ar: 'IBAN', en: 'IBAN', placeholder: 'AE480400008023605457901', mono: true },
            { key: 'bank_name', ar: 'اسم البنك', en: 'Bank Name', placeholder: 'NATIONAL BANK OF RAS AL KHAIMAH' },
            { key: 'branch', ar: 'الفرع', en: 'Branch', placeholder: 'RAKBANK Branch - Al Nakheel' },
            { key: 'swift_code', ar: 'Swift Code', en: 'Swift Code', placeholder: 'NRAKAEAK', mono: true },
          ].map((f) => (
            <div key={f.key}>
              <Label className="text-[#A1A1AA] text-xs">{language === 'ar' ? f.ar : f.en}</Label>
              <Input
                value={settings.bank_account?.[f.key] || ''}
                onChange={(e) => setBankField(f.key, e.target.value)}
                placeholder={f.placeholder}
                className={`bg-[#0a0a0a] border-[#2a2a4e] text-white mt-1 ${f.mono ? 'font-mono' : ''}`}
                data-testid={`bank-${f.key}`}
              />
            </div>
          ))}
        </div>

        <Button
          onClick={saveBank}
          disabled={saving}
          className="mt-6 bg-[#d4af37] hover:bg-[#c9a227] text-black"
          data-testid="save-bank-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Save className="h-4 w-4 me-2" />}
          {language === 'ar' ? 'حفظ البيانات البنكية' : 'Save Bank Details'}
        </Button>
      </div>
    </div>
  );
}

// =========================================================================
function AddBilledToModal({ open, onClose, onSaved, language }) {
  const [name, setName] = useState('');
  const [trn, setTrn] = useState('');
  const [addressInput, setAddressInput] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setName(''); setTrn(''); setAddressInput(''); };
  const close = () => { reset(); onClose(); };

  const save = async () => {
    if (!name.trim()) { toast.error(language === 'ar' ? 'الاسم مطلوب' : 'Name required'); return; }
    setSaving(true);
    try {
      const address_lines = addressInput.split('\n').map((l) => l.trim()).filter(Boolean);
      await axios.post(
        `${BACKEND_URL}/api/admin/company-settings/billed-to`,
        { name: name.trim(), trn: trn.trim(), address_lines },
        { headers: getAuthHeaders() },
      );
      toast.success(language === 'ar' ? 'تم الإضافة' : 'Added');
      reset();
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="bg-[#1a1a2e] border-white/10 text-white" data-testid="add-billed-to-modal">
        <DialogHeader>
          <DialogTitle>{language === 'ar' ? 'إضافة جهة جديدة' : 'Add new entity'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[#A1A1AA] text-xs">{language === 'ar' ? 'الاسم *' : 'Name *'}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white mt-1" data-testid="bt-name" />
          </div>
          <div>
            <Label className="text-[#A1A1AA] text-xs">TRN</Label>
            <Input value={trn} onChange={(e) => setTrn(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white mt-1 font-mono" data-testid="bt-trn" />
          </div>
          <div>
            <Label className="text-[#A1A1AA] text-xs">{language === 'ar' ? 'العنوان (سطر لكل خط)' : 'Address (one line per row)'}</Label>
            <textarea
              value={addressInput}
              onChange={(e) => setAddressInput(e.target.value)}
              rows={3}
              className="w-full mt-1 bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-md p-2 text-sm"
              data-testid="bt-address"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} className="border-white/10 text-white">
            <X className="h-4 w-4 me-1" />{language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#d4af37] hover:bg-[#c9a227] text-black" data-testid="bt-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'ar' ? 'إضافة' : 'Add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
